/* PastéBCN — Related products carousel
   Web Component <pb-related-carousel>:
   - Scroll horizontal con scroll-snap (CSS).
   - Flechas prev/next que avanzan un "page" (ancho del track visible).
   - Las flechas se ocultan en mobile (touch) y cuando no hay overflow.
   - Soporta drag con mouse en desktop (touch ya nativo).
   - Respeta prefers-reduced-motion (sin smooth scroll).
   - Sobrevive a shopify:section:load. */

if (!customElements.get('pb-related-carousel')) {
  class PbRelatedCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-related-track]');
      this.prevBtn = this.querySelector('[data-related-prev]');
      this.nextBtn = this.querySelector('[data-related-next]');

      if (!this.track) return;

      this._onScroll = this._onScroll.bind(this);
      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onResize = this._onResize.bind(this);

      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);

      /* Drag horizontal con mouse en desktop. Touch ya funciona nativo. */
      this.track.addEventListener('pointerdown', this._onPointerDown);

      this._updateArrows();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
      this.track.removeEventListener('pointerdown', this._onPointerDown);
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
    }

    _reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    _pageDelta() {
      /* Cantidad a desplazar = ancho visible del track (clientWidth).
         Esto desplaza una "página completa" de cards. */
      return this.track.clientWidth;
    }

    _onPrev() {
      this.track.scrollBy({
        left: -this._pageDelta(),
        behavior: this._reducedMotion() ? 'auto' : 'smooth',
      });
    }

    _onNext() {
      this.track.scrollBy({
        left: this._pageDelta(),
        behavior: this._reducedMotion() ? 'auto' : 'smooth',
      });
    }

    _onScroll() {
      if (this._scrollTicking) return;
      this._scrollTicking = true;
      window.requestAnimationFrame(() => {
        this._updateArrows();
        this._scrollTicking = false;
      });
    }

    _onResize() {
      this._updateArrows();
    }

    _updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      const scrollLeft = this.track.scrollLeft;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const hasOverflow = maxScroll > 1;

      this.classList.toggle('has-overflow', hasOverflow);
      this.prevBtn.disabled = !hasOverflow || scrollLeft <= 1;
      this.nextBtn.disabled = !hasOverflow || scrollLeft >= maxScroll - 1;
    }

    /* ---- Drag con mouse desktop ---- */
    _onPointerDown(e) {
      /* Solo botón izquierdo, y solo con mouse o pen — touch ya tiene drag nativo. */
      if (e.button !== 0 || e.pointerType === 'touch') return;
      this._dragStartX = e.clientX;
      this._dragStartScrollLeft = this.track.scrollLeft;
      this._dragging = false;
      this._dragMoved = 0;

      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp, { once: true });
    }

    _onPointerMove(e) {
      const dx = e.clientX - this._dragStartX;
      this._dragMoved = Math.abs(dx);
      if (this._dragMoved > 5) {
        this._dragging = true;
        this.classList.add('is-dragging');
        this.track.scrollLeft = this._dragStartScrollLeft - dx;
      }
    }

    _onPointerUp() {
      window.removeEventListener('pointermove', this._onPointerMove);
      this.classList.remove('is-dragging');
      if (this._dragging) {
        /* Previene click fantasma en la card si arrastraste */
        const block = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        };
        this.track.addEventListener('click', block, { capture: true, once: true });
      }
      this._dragging = false;
    }
  }

  customElements.define('pb-related-carousel', PbRelatedCarousel);
}
